import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Trash2, Eye, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../api/adminApi';

const MessagesPage = () => {
  const [data, setData] = useState({ messages: [], total: 0, page: 1, unread_count: 0 });
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/admin/messages', { params: { page, limit: 20, unread_only: unreadOnly } });
      setData(r.data);
    } catch { toast.error('Failed to load messages'); }
    finally { setLoading(false); }
  }, [page, unreadOnly]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const markRead = async (id) => {
    await adminApi.patch(`/admin/messages/${id}/read`).catch(() => {});
    fetchMessages();
  };

  const deleteMsg = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await adminApi.delete(`/admin/messages/${id}`);
      toast.success('Message deleted');
      if (selected?.id === id) setSelected(null);
      fetchMessages();
    } catch { toast.error('Delete failed'); }
  };

  const openMessage = async (msg) => {
    setSelected(msg);
    if (!msg.is_read) await markRead(msg.id);
  };

  const totalPages = Math.ceil(data.total / 20);

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Messages</h1>
          <p className="text-white/40 text-sm mt-0.5">{data.unread_count} unread · {data.total} total</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={unreadOnly} onChange={e => { setUnreadOnly(e.target.checked); setPage(1); }} className="accent-[#D4AF37] w-4 h-4" />
          <span className="text-white/60 text-sm">Unread only</span>
        </label>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* List */}
        <div className="lg:col-span-2 bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm overflow-hidden">
          {loading && <p className="text-white/40 text-sm text-center py-8">Loading...</p>}
          {!loading && data.messages.length === 0 && <p className="text-white/40 text-sm text-center py-8">No messages</p>}
          <div className="divide-y divide-[rgba(212,175,55,0.08)]">
            {data.messages.map(msg => (
              <div
                key={msg.id}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${selected?.id === msg.id ? 'bg-[rgba(212,175,55,0.1)]' : 'hover:bg-white/5'}`}
                onClick={() => openMessage(msg)}
              >
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${msg.is_read ? 'bg-transparent' : 'bg-[#D4AF37]'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${msg.is_read ? 'text-white/70' : 'text-white font-medium'}`}>{msg.name}</p>
                  <p className="text-white/40 text-xs truncate">{msg.subject}</p>
                  <p className="text-white/30 text-[10px] mt-0.5">{formatDate(msg.timestamp)}</p>
                </div>
                <button onClick={e => { e.stopPropagation(); deleteMsg(msg.id); }} className="text-white/20 hover:text-red-400 transition-colors mt-1 flex-shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[rgba(212,175,55,0.1)]">
              <button onClick={() => setPage(p => p - 1)} disabled={page === 1} className="text-white/40 hover:text-white disabled:opacity-20 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-white/40 text-xs">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} className="text-white/40 hover:text-white disabled:opacity-20 transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm">
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-white/30">
              <Mail className="w-8 h-8 mb-2" />
              <p className="text-sm">Select a message to read</p>
            </div>
          ) : (
            <div className="p-6">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-white font-semibold">{selected.subject}</h3>
                  <p className="text-[#D4AF37] text-sm mt-0.5">{selected.name} — <span className="text-white/50">{selected.email}</span></p>
                  <p className="text-white/30 text-xs mt-0.5">{formatDate(selected.timestamp)}</p>
                </div>
                <div className="flex gap-2">
                  <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.3)] text-[#D4AF37] text-xs rounded-sm hover:bg-[rgba(212,175,55,0.2)] transition-colors">
                    <Mail className="w-3.5 h-3.5" />Reply
                  </a>
                  <button onClick={() => deleteMsg(selected.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 text-xs rounded-sm hover:bg-red-500/20 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />Delete
                  </button>
                </div>
              </div>
              <div className={`text-white/70 text-sm leading-relaxed whitespace-pre-wrap bg-[#1A1A1A] rounded-sm p-4 border border-[rgba(212,175,55,0.1)]`}>
                {selected.message}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
